import React from 'react';
import { motion } from 'framer-motion';
import { Droplets, Sprout, ArrowRight, Sparkles, Clock } from 'lucide-react';

interface FeaturedPlatformsCarouselProps {
  onOpenCanales: () => void;
  onOpenRiego: () => void;
}

interface Platform {
  id: string;
  name: string;
  tagline: string;
  description: string;
  features: string[];
  image: string;
  icon: React.ReactNode;
  gradient: string;
  accentColor: string;
  bgPattern: string;
  status: 'available' | 'coming-soon';
  cta: string;
}

const platforms: Platform[] = [
{
  id: 'opencanales',
  name: 'openCanales',
  tagline: 'Hidráulica de Canales',
  description: 'Cálculo y diseño de canales de riego: caudales, flujos normales, críticos y saltos hidráulicos.',
  features: ['10+ Calculadoras', 'Exportar PNG', 'App HFCanales'],
  image: 'https://images.unsplash.com/photo-1504297050568-910d24c426d3?w=800&q=80',
  icon: <Droplets className="w-6 h-6" />,
  gradient: 'from-blue-600 via-cyan-600 to-teal-500',
  accentColor: 'text-cyan-300',
  bgPattern: 'from-blue-900 via-blue-800 to-cyan-900',
  status: 'available',
  cta: 'Explorar'
},
{
  id: 'openriego',
  name: 'openRiego',
  tagline: 'Diseño y Gestión de Riego',
  description: 'Diseño, programación y optimización de sistemas de riego para agricultura de precisión.',
  features: ['Programación', 'Diseño hidráulico', 'Eficiencia'],
  image: 'https://images.unsplash.com/photo-1530267981375-f0de937f5f13?w=800&q=80',
  icon: <Sprout className="w-6 h-6" />,
  gradient: 'from-emerald-600 via-green-600 to-lime-500',
  accentColor: 'text-emerald-300',
  bgPattern: 'from-emerald-900 via-green-800 to-teal-900',
  status: 'available',
  cta: 'Explorar'
}];


const FeaturedPlatformsCarousel: React.FC<FeaturedPlatformsCarouselProps> = ({ onOpenCanales, onOpenRiego }) => {
  const handleAction = (platform: Platform) => {
    if (platform.id === 'opencanales') {
      onOpenCanales();
    } else if (platform.id === 'openriego') {
      onOpenRiego();
    }
  };

  return (
    <div className="mb-20">
      {/* Section Header */}
      <div className="mb-8">
        



        <h2 className="text-3xl font-bold text-gray-900">Ecosistema OpenHH</h2>
        <p className="text-gray-600 mt-1">Suites integrales para gestión hídrica y agrícola</p>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {platforms.map((platform, idx) => {
          const isAvailable = platform.status === 'available';
          return (
            <motion.div
              key={platform.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              onClick={() => isAvailable && handleAction(platform)}
              className={`group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 ${
              isAvailable ? 'cursor-pointer hover:-translate-y-1' : 'cursor-not-allowed'}`
              }>

              {/* Background */}
              <div className={`relative h-[280px] bg-gradient-to-br ${platform.bgPattern}`}>
                {/* Background image */}
                <div className="absolute inset-0">
                  <img
                    src={platform.image}
                    alt={platform.name}
                    className="w-full h-full object-cover opacity-25 mix-blend-overlay" />

                  <div className={`absolute inset-0 bg-gradient-to-r ${platform.bgPattern} opacity-80`}></div>
                  {/* Decorative blobs */}
                  <div className={`absolute -top-16 -right-16 w-64 h-64 rounded-full bg-gradient-to-br ${platform.gradient} opacity-30 blur-3xl group-hover:opacity-40 transition-opacity`}></div>
                  <div className={`absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-gradient-to-br ${platform.gradient} opacity-20 blur-3xl`}></div>
                </div>

                {/* Status badge */}
                {!isAvailable &&
                <div className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-400/20 text-amber-200 border border-amber-400/40 backdrop-blur-sm">
                    <Clock className="w-3 h-3" />
                    Próximamente
                  </div>
                }

                {/* Content */}
                <div className="relative h-full p-6 flex flex-col justify-between text-white">
                  {/* Top: Icon + name */}
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${platform.gradient} flex items-center justify-center shadow-xl ring-2 ring-white/10`}>
                        {platform.icon}
                      </div>
                      <div>
                        <p className={`${platform.accentColor} text-[10px] font-bold uppercase tracking-widest`}>
                          {platform.tagline}
                        </p>
                        <h3 className="text-2xl font-black tracking-tight">{platform.name}</h3>
                      </div>
                    </div>

                    <p className="text-sm text-blue-100/90 leading-relaxed mb-4 line-clamp-2">
                      {platform.description}
                    </p>

                    {/* Features */}
                    <div className="flex flex-wrap gap-1.5">
                      {platform.features.map((feat) =>
                      <span
                        key={feat}
                        className="px-2.5 py-1 text-[10px] font-semibold rounded-full bg-white/10 backdrop-blur-sm text-white border border-white/20">

                          {feat}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bottom: CTA */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                    <span className="text-xs text-white/60">
                      {isAvailable ? 'Plataforma disponible' : 'En desarrollo'}
                    </span>
                    <div
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all ${
                      isAvailable ?
                      'bg-white text-gray-900 group-hover:gap-3 shadow-lg' :
                      'bg-white/10 text-white/60 border border-white/20'}`
                      }>

                      <span>{platform.cta}</span>
                      {isAvailable && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>);

        })}
      </div>
    </div>);

};

export default FeaturedPlatformsCarousel;